import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
    Music2, 
    Users, 
    Award, 
    Heart,
    Star,
    MapPin,
    Calendar,
    Instagram,
    Youtube,
    Quote
} from 'lucide-react';

const bandMembers = [
    {
        name: 'Yash Singh Thakur',
        role: 'Lead Vocalist',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/0tqq5lae_7.jpeg',
        bio: 'The soul and voice of Hawayein with 8+ years of experience.',
    },
    {
        name: 'Band Member',
        role: 'Lead Guitarist',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/zrs4fm4l_6.jpeg',
        bio: 'Bringing melodies to life with electrifying guitar solos.',
    },
    {
        name: 'Band Member',
        role: 'Bassist',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/ae7t0urh_13.jpeg',
        bio: 'The groove master keeping the rhythm tight.',
    },
    {
        name: 'Band Member',
        role: 'Drummer',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/rz7ko0bw_14.jpeg',
        bio: 'The heartbeat of our performances with powerful rhythms.',
    },
];

const stats = [
    { icon: Music2, value: '2000+', label: 'Live Shows' },
    { icon: Users, value: '2M+', label: 'Happy Audience' },
    { icon: Award, value: '15+', label: 'Awards' },
    { icon: Star, value: '8+', label: 'Years Experience' },
];

const AboutPage = () => {
    return (
        <main className="min-h-screen pt-24 pb-16 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Page Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center gap-2 mb-4">
                        <Heart className="w-5 h-5 text-primary" />
                        <span className="text-sm font-body font-medium text-primary uppercase tracking-wider">
                            Our Story
                        </span>
                    </div>
                    <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
                        About Hawayein The Band
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg font-body max-w-3xl mx-auto">
                        India's Premier <span className="text-primary font-semibold">Sufi & Bollywood Live Band</span> — 
                        Led by Yash Singh Thakur with 2000+ unforgettable performances across India since 2016
                    </p>
                </motion.div>

                {/* Story Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="max-w-4xl mx-auto mb-20"
                >
                    <Card className="card-premium rounded-3xl overflow-hidden">
                        <CardContent className="p-0">
                            <div className="grid grid-cols-1 lg:grid-cols-2">
                                <div className="relative h-64 lg:h-auto">
                                    <img
                                        src="https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/wvv2ruft_16.jpeg"
                                        alt="Hawayein Band - Full Team - Best Sufi Bollywood Band Raipur"
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card lg:bg-gradient-to-r" />
                                </div>
                                <div className="p-8 lg:p-10 flex flex-col justify-center">
                                    <Badge variant="outline" className="w-fit mb-4 border-primary/40 bg-primary/10 text-primary">
                                        🎵 Sufi & Bollywood Specialists • Est. 2016
                                    </Badge>
                                    <h2 className="font-heading font-bold text-2xl text-foreground mb-4">
                                        Feel the Music in the Air
                                    </h2>
                                    <p className="text-muted-foreground font-body leading-relaxed mb-4">
                                        Hawayein, which translates to "the winds" in Hindi, represents the essence of our music — 
                                        free, flowing, and touching every soul it reaches. Founded by <span className="text-primary font-semibold">Yash Singh Thakur</span>, 
                                        our band has grown from small college performances to becoming Central India's most 
                                        sought-after Sufi & Bollywood live band with 2000+ successful shows.
                                    </p>
                                    <p className="text-muted-foreground font-body leading-relaxed">
                                        We specialize in creating personalized musical experiences, whether it's a grand wedding, 
                                        an intimate anniversary, a high-energy corporate event, or an electrifying concert. 
                                        Our versatile repertoire spans Bollywood classics, international hits, regional favorites, 
                                        and contemporary chartbusters.
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20 max-w-4xl mx-auto"
                >
                    {stats.map((stat, index) => (
                        <Card key={index} className="card-premium rounded-2xl text-center">
                            <CardContent className="p-6">
                                <stat.icon className="w-8 h-8 text-primary mx-auto mb-3" />
                                <p className="font-heading font-bold text-2xl text-foreground mb-1">
                                    {stat.value}
                                </p>
                                <p className="text-sm text-muted-foreground font-body">
                                    {stat.label}
                                </p>
                            </CardContent>
                        </Card>
                    ))}
                </motion.div>

                {/* Band Members */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="mb-20"
                >
                    <div className="text-center mb-10">
                        <h2 className="font-heading font-bold text-2xl sm:text-3xl text-foreground mb-3">
                            Meet The Band
                        </h2>
                        <p className="text-muted-foreground font-body">
                            The talented musicians behind every magical performance
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {bandMembers.map((member, index) => (
                            <motion.div
                                key={member.name}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                            >
                                <Card className="card-premium rounded-2xl overflow-hidden group">
                                    <CardContent className="p-0">
                                        <div className="relative h-56 overflow-hidden">
                                            <img
                                                src={member.image}
                                                alt={member.name}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
                                        </div>
                                        <div className="p-5 text-center">
                                            <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
                                                {member.name}
                                            </h3>
                                            <Badge variant="outline" className="mb-2 border-primary/40 text-primary text-xs">
                                                {member.role}
                                            </Badge>
                                            <p className="text-sm text-muted-foreground font-body">
                                                {member.bio}
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Mission Quote */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="max-w-3xl mx-auto mb-16"
                >
                    <Card className="card-premium rounded-3xl bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
                        <CardContent className="p-8 lg:p-10 text-center">
                            <Quote className="w-12 h-12 text-primary/30 mx-auto mb-4" />
                            <p className="font-heading text-xl sm:text-2xl text-foreground italic mb-4">
                                "Our mission is simple — to create moments that become memories, 
                                to turn events into experiences, and to ensure that every note 
                                we play resonates in your heart forever."
                            </p>
                            <p className="text-primary font-heading font-semibold">
                                — Yash Singh Thakur, Founder
                            </p>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="text-center"
                >
                    <p className="text-muted-foreground font-body mb-4">
                        Ready to experience the magic of Hawayein?
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/book">
                            <Button className="bg-gradient-to-r from-primary to-accent text-foreground font-heading font-semibold px-8 py-6 rounded-xl">
                                Book Us Now
                            </Button>
                        </Link>
                        <Link to="/performances">
                            <Button variant="outline" className="border-border/50 text-foreground font-heading font-semibold px-8 py-6 rounded-xl hover:bg-card/50">
                                Watch Performances
                            </Button>
                        </Link>
                    </div>
                </motion.div>
            </div>
        </main>
    );
};

export default AboutPage;
